import { useDemo } from '../../api/app-context';
import { useRunHistory, useResumeRun } from '../../api/hooks';
import { hasCode, loadErrorLine, resumeErrorLine } from '../../api/errors';
import { runHistoryLabel } from '@ni/shared';
import {
  RESEARCH_STATE_WORDS,
  RUN_STATE_LABELS,
  RUN_STOPPED,
  RUN_UNDELIVERED_HEAD,
  undeliveredLine,
} from '../../lib/copy';
import { formatDay } from '../../lib/format';
import { isCancelledRun, isOfficeEndedRun, offersResume } from '../../lib/run-state';
import { ResumeButton } from '../resume-button';
import { Loading } from '../ui/loading';
import { EmptyLine } from '../ui/empty-line';

/**
 * Run history, a quiet disclosure in the foot (ui-design §4.7 region 7). It
 * carries no cost: research spend is admin-plane only (amended 2026-08-27), and
 * the office sees what a run did, not what it cost. A run the user never started —
 * `created_by_user_id` null — reads "continued automatically", which is what
 * makes the auto-continue seam legible after the fact.
 */
export function RunHistory({ subjectId }: { subjectId: string }) {
  const demo = useDemo();
  const history = useRunHistory(subjectId);
  const resume = useResumeRun(subjectId);

  if (history.isPending)
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  if (history.isError) return <EmptyLine>{loadErrorLine(history.error)}</EmptyLine>;
  if (history.data.entries.length === 0) return <EmptyLine>No runs yet.</EmptyLine>;

  return (
    <div>
      {history.data.entries.map((run) => {
        /* **Both endings the office made itself**, and the split is the strip's
           own (`run-strip.tsx`): a withdrawal and a cancel are different events
           and read differently, and neither of them is "Failed" with a Resume
           beside it. Before this the foot called a cancelled run failed and
           offered a Resume the server answers 409 to — a button that does
           nothing, silently, because the page swallows that one code. */
        const officeEnded = isOfficeEndedRun(run);
        const undelivered = run.state === 'complete' && run.delivered === false;
        return (
          <div className="act-row" key={run.id}>
            <div className="act-name">
              {runHistoryLabel(run)}
              {run.created_by_user_id === null ? (
                <div className="tag-faint block">continued automatically</div>
              ) : null}
            </div>
            <div
              className={run.state === 'complete' && !undelivered ? 'act-state done' : 'act-state'}
            >
              {isCancelledRun(run)
                ? RUN_STOPPED
                : officeEnded
                  ? 'Stopped · wrong person'
                  : undelivered
                    ? `${RUN_UNDELIVERED_HEAD} · ${undeliveredLine(run.kind)}`
                    : run.resume?.status === 'queued'
                      ? RESEARCH_STATE_WORDS.resuming
                      : RUN_STATE_LABELS[run.state]}
            </div>
            <div className="act-value">{formatDay(run.completed_at ?? run.created_at)}</div>
            {!demo && offersResume(run) ? <ResumeButton runId={run.id} resume={resume} /> : null}
          </div>
        );
      })}

      {resume.isError && !hasCode(resume.error, 'RUN_NOT_RESUMABLE') ? (
        <div className="src">{resumeErrorLine(resume.error)}</div>
      ) : null}
    </div>
  );
}
